/**
 * Result of splitting email HTML content into latest reply and previous chain
 */
export interface SplitEmailResult {
  /** The latest email/reply content (new message) */
  latestContent: string;
  /** The previous replies in the email chain (quoted/historical content) */
  previousChain: string;
}

/**
 * Decodes quoted-printable encoded text commonly found in email HTML content.
 * Handles:
 * - Encoded equals signs (=3D → =)
 * - Hex-encoded characters (=E2=80=AF → Unicode character)
 * - Soft line breaks (= at end of line)
 * - UTF-8 multi-byte sequences
 *
 * @param text - Quoted-printable encoded text
 * @returns Decoded text
 */
function decodeQuotedPrintable(text: string): string {
  let decoded = text;

  // Handle soft line breaks (= followed by newline should be removed)
  decoded = decoded.replace(/=\r?\n/g, "");

  // Decode hex sequences (=XX where XX is hex)
  // Handle UTF-8 multi-byte sequences properly
  decoded = decoded.replace(/(?:=([0-9A-F]{2}))+/gi, (match) => {
    // Extract all hex pairs from the match
    const hexPairs = match.match(/([0-9A-F]{2})/gi);
    if (!hexPairs) return match;

    // Convert hex pairs to byte array
    const bytes = hexPairs.map((hex) => parseInt(hex, 16));

    // Decode UTF-8 bytes to string
    try {
      // Use TextDecoder for proper UTF-8 decoding
      if (typeof TextDecoder !== "undefined") {
        const decoder = new TextDecoder("utf-8");
        return decoder.decode(new Uint8Array(bytes));
      }

      // Fallback for environments without TextDecoder
      // This handles single-byte characters correctly
      return bytes.map((byte) => String.fromCharCode(byte)).join("");
    } catch {
      // If decoding fails, return original match
      return match;
    }
  });

  return decoded;
}

/**
 * Finds the index where the email content should be split between
 * the latest reply and the previous email chain.
 *
 * Supports multiple email client formats:
 * - Gmail: <blockquote> tags and gmail_quote classes
 * - Outlook: Horizontal rule dividers and reply headers
 * - Apple Mail: <blockquote type="cite">
 * - Yahoo Mail: Quoted content dividers
 * - Generic: Common attribution patterns
 *
 * IMPORTANT: Only splits on blockquotes with reply-specific indicators to avoid
 * incorrectly treating user-added blockquotes (for emphasis/citation) as previous replies.
 *
 * @param html - HTML content to search
 * @returns Index where split should occur, or null if no split point found
 */
function findEmailSplitPoint(html: string): number | null {
  // Strategy 1: Gmail-specific - gmail_quote or gmail_quote_container class
  // Check this BEFORE blockquote since Gmail uses both and we want the outer container
  const gmailQuoteMatch = html.match(/<div[^>]*class=[^>]*gmail_quote/i);
  if (gmailQuoteMatch && gmailQuoteMatch.index !== undefined) {
    return gmailQuoteMatch.index;
  }

  // Strategy 2: Reply-specific blockquotes with indicators
  // Only match blockquotes that have reply-specific attributes/classes
  // to avoid splitting on user-added blockquotes that are part of current content

  // 2a. Apple Mail: <blockquote type="cite">
  const appleMailBlockquoteMatch = html.match(/<blockquote[^>]*type\s*=\s*["']cite["']/i);
  if (appleMailBlockquoteMatch && appleMailBlockquoteMatch.index !== undefined) {
    return appleMailBlockquoteMatch.index;
  }

  // 2b. Gmail/email clients: blockquote with reply-specific classes
  const replyBlockquoteMatch = html.match(
    /<blockquote[^>]*class\s*=\s*["'][^"']*(?:gmail_quote|yahoo_quoted|moz-cite-prefix)[^"']*["']/i
  );
  if (replyBlockquoteMatch && replyBlockquoteMatch.index !== undefined) {
    return replyBlockquoteMatch.index;
  }

  // Strategy 3: Outlook - horizontal rule divider with specific styling
  const outlookDividerMatch = html.match(/<div[^>]*style="[^"]*border(-top)?:[^"]*solid[^"]*#[Ee]1[Ee]1[Ee]1/i);
  if (outlookDividerMatch && outlookDividerMatch.index !== undefined) {
    return outlookDividerMatch.index;
  }

  // Strategy 4: Outlook - reply divider with id
  const outlookReplyMatch = html.match(/<div[^>]*id="[^"]*divRplyFwdMsg[^"]*"/i);
  if (outlookReplyMatch && outlookReplyMatch.index !== undefined) {
    return outlookReplyMatch.index;
  }

  // Strategy 5: Yahoo Mail - quoted content wrapper
  const yahooQuoteMatch = html.match(/<div[^>]*class="[^"]*yahoo_quoted/i);
  if (yahooQuoteMatch && yahooQuoteMatch.index !== undefined) {
    return yahooQuoteMatch.index;
  }

  // Strategy 6: Generic attribution patterns
  // Look for "On [date]...wrote:" or "From:...Sent:...To:" patterns
  const attributionPatterns = [
    // "On Tue, Jan 6, 2026 at 1:33 PM..." (Gmail style)
    /On\s+\w+,\s+\w+\s+\d+,\s+\d{4}\s+at\s+\d+:\d+/i,
    // "On [date]...wrote:" (Generic)
    /On\s+[\w\s,]+\d{4}.*wrote:/i,
    // "From:...Sent:...To:" (Outlook style in HTML)
    /<b>From:<\/b>.*<b>Sent:<\/b>.*<b>To:<\/b>/is,
    // "-------- Original Message --------"
    /[-_]{3,}\s*Original Message\s*[-_]{3,}/i,
    // "---------- Forwarded message ---------"
    /[-_]{3,}\s*Forwarded message\s*[-_]{3,}/i,
  ];

  for (const pattern of attributionPatterns) {
    const match = html.match(pattern);
    if (match && match.index !== undefined) {
      // Look backward from the match to find the start of the containing element
      // This ensures we include the full attribution block
      const beforeMatch = html.substring(0, match.index);
      const beforeMatchLower = beforeMatch.toLowerCase();
      const lastDivMatch = beforeMatchLower.lastIndexOf("<div");
      const lastBrMatch = beforeMatchLower.lastIndexOf("<br");

      // Use the closest opening tag before the attribution
      const splitIndex = Math.max(lastDivMatch, lastBrMatch);
      if (splitIndex > 0) {
        return splitIndex;
      }
      return match.index;
    }
  }

  // Strategy 7: Look for horizontal rule (<hr>) as a separator
  const hrMatch = html.match(/<hr[\s/>]/i);
  if (hrMatch && hrMatch.index !== undefined) {
    return hrMatch.index;
  }

  // Strategy 8: Bare blockquote as last resort (only if preceded by attribution)
  // Check if there's a blockquote that comes shortly after attribution text
  const bareBlockquoteMatch = html.match(/<blockquote[\s>]/i);
  if (bareBlockquoteMatch && bareBlockquoteMatch.index !== undefined) {
    // Only use this as split point if it's preceded by attribution-like text
    // Look back up to 300 characters before the blockquote
    const lookbackStart = Math.max(0, bareBlockquoteMatch.index - 300);
    const textBeforeBlockquote = html.substring(lookbackStart, bareBlockquoteMatch.index);

    // Check if there's attribution text right before the blockquote
    const hasAttributionBefore =
      /On\s+\w+,\s+\w+\s+\d+,\s+\d{4}/i.test(textBeforeBlockquote) ||
      /On\s+[\w\s,]+\d{4}.*wrote:/i.test(textBeforeBlockquote) ||
      /From:.*Sent:.*To:/is.test(textBeforeBlockquote) ||
      /Original Message/i.test(textBeforeBlockquote) ||
      /Forwarded message/i.test(textBeforeBlockquote);

    if (hasAttributionBefore) {
      return bareBlockquoteMatch.index;
    }
  }

  // No split point found - entire content is latest message
  return null;
}

/**
 * Removes trailing <br> tags from HTML content.
 * Handles various formats: <br>, <br/>, <br />, <BR>, etc.
 *
 * @param html - HTML content
 * @returns HTML with trailing <br> tags removed
 */
function removeTrailingBrTags(html: string): string {
  // Remove trailing <br> tags (with optional closing slash and whitespace)
  // Matches: <br>, <br/>, <br />, <BR>, etc. at the end of the string
  return html.replace(/(<br\s*\/?\s*>)+\s*$/gi, "").trim();
}

/**
 * Splits email HTML content into the latest reply and the previous email chain.
 *
 * This function handles:
 * - Quoted-printable encoding (=3D, =XX hex sequences)
 * - Multiple email client formats (Gmail, Outlook, Apple Mail, Yahoo, etc.)
 * - Attribution text ("On [date]...wrote:") - included in previous chain
 * - Nested replies (all go into previous chain)
 * - Trailing <br> tags (removed from latest content)
 * - Edge cases (empty content, no previous chain, malformed HTML)
 *
 * @param htmlContent - Raw HTML content from email
 * @returns Object with latestContent and previousChain, both trimmed
 *
 * @example
 * ```typescript
 * const email = getEmail();
 * const { latestContent, previousChain } = splitEmailContent(email.HTMLContent);
 *
 * // Display latest content prominently
 * console.log("New reply:", latestContent);
 *
 * // Show previous chain in collapsed/secondary view
 * console.log("Previous emails:", previousChain);
 * ```
 *
 * @example
 * ```typescript
 * // Gmail reply with quoted-printable encoding
 * const gmailHtml = '<div dir=3D"ltr">This is a response</div><br><div class=3D"gmail_quote">...';
 * const result = splitEmailContent(gmailHtml);
 * // result.latestContent: '<div dir="ltr">This is a response</div>'
 * // result.previousChain: '<div class="gmail_quote">...'
 * ```
 *
 * @example
 * ```typescript
 * // Original email with no previous chain
 * const originalEmail = '<span>This is an email</span><br/><br/><img src="..."/>';
 * const result = splitEmailContent(originalEmail);
 * // result.latestContent: '<span>This is an email</span><br/><br/><img src="..."/>'
 * // result.previousChain: ''
 * ```
 */
export function splitEmailContent(htmlContent: string): SplitEmailResult {
  // Handle empty/null input
  if (!htmlContent || htmlContent.trim().length === 0) {
    return {
      latestContent: "",
      previousChain: "",
    };
  }

  // Decode quoted-printable encoding
  const decodedHtml = decodeQuotedPrintable(htmlContent);

  // Find the split point
  const splitIndex = findEmailSplitPoint(decodedHtml);

  // If no split point found, entire content is the latest message
  if (splitIndex === null) {
    return {
      latestContent: removeTrailingBrTags(decodedHtml.trim()),
      previousChain: "",
    };
  }

  // Split the content at the identified point
  const latestContent = removeTrailingBrTags(decodedHtml.substring(0, splitIndex).trim());
  const previousChain = decodedHtml.substring(splitIndex).trim();

  return {
    latestContent,
    previousChain,
  };
}
